import { createContext, useContext } from "react";
import { LayoutNode } from "../layout";
import { OrgEmployeeData, OrgUnitTypeData } from "../types";

/**
 * Узлы React Flow получают в data только свою раскладку. Общее состояние дерева
 * (наведение, поиск, перетаскивание) и обработчики — через контекст: обработчики
 * стабильны, а состояние меняется одним значением на всё дерево.
 */

export type DropMode = "inside" | "before" | "after" | "invalid";

export interface TreeActions {
    toggle(id: string): void;
    openUnit(id: string): void;
    openEmployee(id: string): void;
    hover(id: string | null): void;
    startRename(id: string): void;
    commitRename(id: string, name: string | null): void;
    addUnit(parentId: string, anchor: HTMLElement): void;
    addEmployee(unitId: string, positionId?: string): void;
    contextMenu(id: string, event: React.MouseEvent): void;
}

export interface TreeState {
    edit: boolean;
    path: Set<string>;
    /** карточка под курсором */
    hovered: string | null;
    /** только что добавленный узел — короткая подсветка */
    flash: string | null;
    hits: Set<string>;
    /** при поиске — узлы, которые не совпали и не ведут к совпадению */
    dimmed: Set<string> | null;
    renaming: string | null;
    drop: { id: string; mode: DropMode } | null;
    dragging: string | null;
    canOpenEmployee: boolean;
    canEditEmployee: boolean;
    companyName: string;
    typesById: Map<string, OrgUnitTypeData>;
    employeesById: Map<string, OrgEmployeeData>;
    /** подразделение → его руководитель */
    managerByUnit: Map<string, string>;
    /** адрес API для фото сотрудников */
    apiBase: string;
}

export interface NodeData extends Record<string, unknown> {
    layout: LayoutNode;
    /** задержка анимации появления — каскадом по глубине */
    delay: number;
    leaving: boolean;
}

export const TreeActionsContext = createContext<TreeActions | null>(null);
export const TreeStateContext = createContext<TreeState | null>(null);

export const useTreeActions = () => useContext(TreeActionsContext)!;
export const useTreeState = () => useContext(TreeStateContext)!;

/** Цвет полосы и иконки узла по уровню: верхние уровни темнее */
export const levelColor = (depth: number) => {
    switch (depth) {
        case 1:
            return "#00273d";
        case 2:
            return "rgba(0, 39, 61, 0.62)";
        case 3:
            return "#36acd7";
        default:
            return "rgba(54, 172, 215, 0.62)";
    }
};

export const initials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
};

/** «Иванов Иван Иванович» → «Иванов И. И.» */
export const shortName = (name: string) => {
    const [last, ...rest] = name.trim().split(/\s+/);
    if (!rest.length) {
        return last ?? "";
    }
    return `${last} ${rest.map((r) => `${r[0]}.`).join(" ")}`;
};

export const plural = (n: number, one: string, few: string, many: string) => {
    const a = Math.abs(n) % 100;
    const b = a % 10;
    if (a > 10 && a < 20) {
        return many;
    }
    if (b > 1 && b < 5) {
        return few;
    }
    return b === 1 ? one : many;
};
